<template>
  <div class="container">
    <HeadingOne class="-margin-md">お客さま情報のご入力</HeadingOne>

    <ErrorsAlert
      :errors="errors"
      :column-only="true"
      :items="inputItemsEn"
    ></ErrorsAlert>

    <WizardGroup>
      <MainWizard :active-index="0" type="entry"></MainWizard>
      <SubWizard :active-index="2" type="usernew"></SubWizard>
    </WizardGroup>

    <form novalidate @submit.prevent="submitValidateDebt">
      <section>
        <TableGroup>
          <HeadingTwo>お借り入れ状況について</HeadingTwo>
          <div class="_mb-10">
            <small>※必須の項目は必ずご入力ください。</small>
          </div>

          <TableBox class="-entry">
            <TableBody>
              <TableRow>
                <TableHeader badge="必須" badge-size="-sm" class="-hastooltip"
                  >他社のお借り入れ状況<small
                    >（件数）<small class="moji">【半角数字】</small></small
                  >
                  <TooltipsItem>
                    他社のお借り入れがない場合は0を入力してください。<br />住宅・自動車ローンは除外した件数を入力してください。
                  </TooltipsItem>
                </TableHeader>
                <TableData :has-error="errors['other_loan_count']">
                  <InputGroup
                    :errors="errors"
                    :prop-names="['other_loan_count']"
                  >
                    <InputgroupAppend>
                      <InputItem
                        id="other_loan_count"
                        v-model="userData.other_loan_count"
                        type="text"
                        class="-sm"
                        :maxlength="2"
                        tracking-name="en004_other_loan_count"
                      ></InputItem>
                      <InputAppend>件</InputAppend>
                    </InputgroupAppend>
                    <InputLabel>入力例：1件の場合「1」</InputLabel>
                  </InputGroup>
                </TableData>
              </TableRow>

              <TableRow>
                <TableHeader badge="必須" badge-size="-sm" class="-hastooltip"
                  >他社のお借り入れ状況<small
                    >（金額）<small class="moji">【半角数字】</small></small
                  >
                  <TooltipsItem>
                    他社のお借り入れがない場合は0を入力してください。<br />住宅・自動車ローンは除外した金額を入力してください。<br />金額は1万円単位で入力してください。
                  </TooltipsItem>
                </TableHeader>
                <TableData :has-error="errors['other_loan_amount']">
                  <InputGroup
                    :errors="errors"
                    :prop-names="['other_loan_amount']"
                  >
                    <InputgroupAppend>
                      <InputItem
                        id="input_other_loan_amount"
                        v-model="input_other_loan_amount"
                        type="text"
                        class="-sm"
                        :maxlength="4"
                        tracking-name="en004_input_other_loan_amount"
                      ></InputItem>
                      <InputAppend>万円</InputAppend>
                    </InputgroupAppend>
                    <InputLabel
                      >10万円の場合「10」※1万円単位、1万円未満の端数は切り捨てとなります。</InputLabel
                    >
                  </InputGroup>
                </TableData>
              </TableRow>

              <TableRow>
                <TableHeader badge="必須" badge-size="-sm"
                  >ご希望のご利用限度額</TableHeader
                >
                <TableData :has-error="errors['asking_loan_amount']">
                  <InputGroup
                    :errors="errors"
                    :prop-names="['asking_loan_amount']"
                  >
                    <BtngroupRadio>
                      <BtnRadio
                        v-for="(askingLoanAmount, index) in askingLoanAmounts"
                        :id="'asking_loan_amount' + index"
                        :key="'askingLoanAmount' + index"
                        v-model="userData.asking_loan_amount"
                        name="askingLoanAmount"
                        :value="askingLoanAmount['value']"
                        :label="askingLoanAmount['name']"
                        :tracking-name="`en004_asking_loan_amount_${index}`"
                        class="btn-radio-loan_amount"
                      >
                      </BtnRadio>
                      <BtnRadio
                        id="asking_loan_amount999"
                        key="askingLoanAmount999"
                        v-model="userData.asking_loan_amount"
                        name="その他"
                        class="_font-sm"
                        value="other"
                        tracking-name="en004_asking_loan_amount_999"
                      >
                        その他
                        <InputgroupAppend>
                          <InputItem
                            id="input_other_loan_amount_other"
                            v-model="askingLoanAmountOther"
                            type="number"
                            class="-radio"
                            :maxlength="3"
                            tracking-name="en004_asking_loan_amount_other"
                            :disabled="userData.asking_loan_amount !== 'other'"
                          />
                          <InputAppend class="-radio">万円</InputAppend>
                        </InputgroupAppend>
                      </BtnRadio>
                    </BtngroupRadio>
                  </InputGroup>
                  <InputLabel>※10万円〜100万円（10万円単位）</InputLabel>
                </TableData>
              </TableRow>

              <TableRow>
                <TableHeader>毎月のご返済日</TableHeader>
                <TableData :has-error="errors['repayment_date']">
                  <InputGroup :errors="errors" :prop-names="['repayment_date']">
                    <BtngroupRadio>
                      <BtnRadio
                        v-for="(repaymentDate, index) in repaymentDates"
                        :id="'repayment_date' + index"
                        :key="'repaymentDate' + index"
                        v-model="userData.repayment_date"
                        name="repaymentDate"
                        :value="repaymentDate['value']"
                        :label="repaymentDate['name']"
                        :tracking-name="`en004_repayment_date_${index}`"
                      >
                      </BtnRadio>
                    </BtngroupRadio>
                  </InputGroup>
                </TableData>
              </TableRow>

              <TableRow>
                <TableHeader badge="必須" badge-size="-sm"
                  >お取引の目的</TableHeader
                >
                <TableData :has-error="errors['purpose_of_use']">
                  <InputGroup :errors="errors" :prop-names="['purpose_of_use']">
                    <InputLabel class="_mb-5"
                      >本サービスは生計費融資となり、事業費融資を目的としたお申し込みはお受けできません。</InputLabel
                    >
                    <InputCheck
                      id="purpose_of_use"
                      v-model="userData.purpose_of_use"
                      value="確認しました"
                      tracking-name="en004_purpose_of_use"
                      >確認しました</InputCheck
                    >
                  </InputGroup>
                </TableData>
              </TableRow>
            </TableBody>
            <!-- TableBody -->
          </TableBox>
        </TableGroup>

        <BtngroupDouble class="-margin-lg">
          <template #main>
            <BtnMain
              class="-main"
              tracking-name="en004_next_button"
              @click.prevent="submitValidateDebt"
            >
              確認画面へ
            </BtnMain>
          </template>
          <template #back>
            <BtnBack
              class="-back -sm"
              router-name="en003"
              tracking-name="en004_back_button"
            >
              戻る
            </BtnBack>
          </template>
        </BtngroupDouble>
      </section>
      <!-- 勤務先情報 -->
    </form>
  </div>
</template>

<script>
import {
  askingLoanAmounts,
  repaymentDates,
  inputItemsEn,
} from "@/constants/constant";
import { initCredentials } from "@/utils/Auth";
import {
  getUserDataLambdaInvoke,
  validateDebtLambdaInvoke,
} from "@/utils/LambdaHelper";
import { setTrackingEventDataSetting } from "@/utils/TrackingDataHelper";
import { setMeta } from "@/utils/Meta";
import { goToErrorPage, scrollTop } from "@/utils/Other";

import HeadingOne from "@/components/heading/HeadingOne";
import WizardGroup from "@/components/wizard/WizardGroup";
import MainWizard from "@/components/wizard/MainWizard";
import SubWizard from "@/components/wizard/SubWizard";
import ErrorsAlert from "@/components/alert/ErrorsAlert";
import TableGroup from "@/components/tables/TableGroup";
import HeadingTwo from "@/components/heading/HeadingTwo";
import TableBox from "@/components/tables/TableBox";
import TableBody from "@/components/tables/TableBody";
import TableRow from "@/components/tables/TableRow";
import TableHeader from "@/components/tables/TableHeader";
import TooltipsItem from "@/components/tooltip/TooltipsItem";
import TableData from "@/components/tables/TableData";
import InputGroup from "@/components/input/InputGroup";
import InputgroupAppend from "@/components/input/InputgroupAppend";
import InputItem from "@/components/input/InputItem";
import InputAppend from "@/components/input/InputAppend";
import InputLabel from "@/components/input/InputLabel";
import BtngroupRadio from "@/components/button/BtngroupRadio";
import BtnRadio from "@/components/button/BtnRadio";
import InputCheck from "@/components/input/InputCheck";
import BtngroupDouble from "@/components/button/BtngroupDouble";
import BtnMain from "@/components/button/BtnMain";
import BtnBack from "@/components/button/BtnBack";

export default {
  name: "En004",
  components: {
    BtnBack,
    BtnMain,
    BtngroupDouble,
    InputCheck,
    BtnRadio,
    BtngroupRadio,
    InputLabel,
    InputAppend,
    InputItem,
    InputgroupAppend,
    InputGroup,
    TableData,
    TooltipsItem,
    TableHeader,
    TableRow,
    TableBody,
    TableBox,
    HeadingTwo,
    TableGroup,
    ErrorsAlert,
    SubWizard,
    MainWizard,
    WizardGroup,
    HeadingOne,
  },
  setup() {
    setMeta({
      title: "お客さま情報のご入力",
      description:
        "au PAY スマートローンはau IDをお持ちのお客様のための便利なカードレスローンサービスです。お申込みは、スマートフォン・パソコンでかんたんWEB完結！24時間お申込み可能。最短30分で審査結果をご連絡します。",
      keywords:
        "au PAY スマートローン,au,カードレスローン,スマートフォン,au PAY 残高,セブン銀行スマホATM,借入,返済,手続き,入会,お借り入れ状況",
    });
  },
  data() {
    return {
      askingLoanAmounts: askingLoanAmounts,
      askingLoanAmountOther: "",
      repaymentDates: repaymentDates,
      errors: {},
      input_other_loan_amount: "",
      isDisabled: false,
      userData: {
        other_loan_count: "",
        other_loan_amount: "",
        asking_loan_amount: "500000",
        repayment_date: "26", // 26日固定
        purpose_of_use: "",
      },
      inputItemsEn: inputItemsEn,
    };
  },
  mounted() {
    try {
      getUserDataLambdaInvoke()
        .then((res) => {
          for (let key in res) {
            if (Object.hasOwnProperty.call(this.userData, key)) {
              this.userData[key] = res[key];
            }
          }
          if (this.userData.other_loan_amount) {
            this.input_other_loan_amount =
              this.userData.other_loan_amount / 10000;
          }
          if (
            !askingLoanAmounts.find(
              (v) => v.value === this.userData.asking_loan_amount
            )
          ) {
            this.askingLoanAmountOther =
              this.userData.asking_loan_amount / 10000;
            this.userData.asking_loan_amount = "other";
          }
          this.$store.dispatch("setSystemAuId", res.system_auid);
        })
        .catch((error) => {
          goToErrorPage(error);
        });
      setTrackingEventDataSetting();
    } catch (e) {
      goToErrorPage(e);
    }
  },
  methods: {
    submitValidateDebt() {
      try {
        if (this.isDisabled) {
          return;
        }
        this.isDisabled = true;
        this.errors = {};
        this.setUserPurposeOfUse();
        this.setOtherLoanAmount();
        const request = { ...this.userData };
        if (this.userData.asking_loan_amount === "other") {
          request.asking_loan_amount = this.askingLoanAmountOther
            ? this.askingLoanAmountOther * 10000
            : "";
        }
        initCredentials()
          .then(() => {
            validateDebtLambdaInvoke(request)
              .then((res) => {
                this.errors = res.errors || {};
                if (Object.keys(this.errors).length > 0) {
                  scrollTop();
                  this.isDisabled = false;
                } else {
                  this.$router.push({ name: "en005" });
                }
              })
              .catch((error) => {
                goToErrorPage(error);
              });
          })
          .catch((error) => {
            goToErrorPage(error);
          });
      } catch (e) {
        goToErrorPage(e);
      }
    },
    setOtherLoanAmount() {
      try {
        let otherLoanAmount = String(this.input_other_loan_amount).trim();
        this.userData.other_loan_amount = otherLoanAmount;
        if (otherLoanAmount && !isNaN(otherLoanAmount)) {
          if (otherLoanAmount.indexOf(".") !== 0) {
            this.userData.other_loan_amount = parseInt(otherLoanAmount) * 10000;
          }
        }
      } catch (e) {
        goToErrorPage(e);
      }
    },
    setUserPurposeOfUse() {
      if (this.userData.purpose_of_use === null) {
        this.userData.purpose_of_use = "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@media screen and (max-width: 767px) {
  .btngroup-radio {
    .btn-radio {
      &.btn-radio-loan_amount {
        margin-right: 0;
      }
    }
  }
}
</style>
